import type { CloudResourceInfo } from './types';
import { addBackendSuffix } from '@/utils/namingConvention';

// Parse cloudResourceInfo JSON string to extract columns
export const parseCloudResourceInfo = (cloudResourceInfoStr?: string): CloudResourceInfo | null => {
  if (!cloudResourceInfoStr) return null;
  try {
    return JSON.parse(cloudResourceInfoStr) as CloudResourceInfo;
  } catch {
    return null;
  }
};

// Helper to quote identifier if it contains non-word characters
export const quoteIdentifier = (name: string): string => {
  return /\W/.test(name) ? `"${name}"` : name;
};

// Construct fully qualified name for datasets (schema: ws{seq}_hub)
export const getDatasetQualifiedName = (workspaceSeq: number, viewName: string): string => {
  const schemaName = `${addBackendSuffix(`ws${workspaceSeq}`)}_hub`;
  return `${quoteIdentifier(schemaName)}.${quoteIdentifier(viewName)}`;
};

// Construct fully qualified name for source tables (schema: ws{seq}_catalog)
export const getSourceTableQualifiedName = (
  workspaceSeq: number,
  viewName: string,
  cloudResourceId?: string,
): string => {
  // Special case for sample_db (dev environment)
  const schemaName = cloudResourceId?.startsWith('beeminternaldev.sample_db.')
    ? `ws${workspaceSeq}_catalog`
    : `${addBackendSuffix(`ws${workspaceSeq}`)}_catalog`;
  return `${quoteIdentifier(schemaName)}.${quoteIdentifier(viewName)}`;
};

// Helper to extract a snippet around the match in SQL query
export const extractSqlSnippet = (sqlQuery: string, searchTerm: string, maxLength = 60): string => {
  const lowerSql = sqlQuery.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase();
  const matchIndex = lowerSql.indexOf(lowerSearch);

  if (matchIndex === -1) return sqlQuery.slice(0, maxLength) + (sqlQuery.length > maxLength ? '...' : '');

  // Calculate start and end positions for the snippet
  const snippetPadding = Math.floor((maxLength - searchTerm.length) / 2);
  let start = Math.max(0, matchIndex - snippetPadding);
  let end = Math.min(sqlQuery.length, matchIndex + searchTerm.length + snippetPadding);

  // Adjust to not cut words if possible
  if (start > 0) {
    const spaceIndex = sqlQuery.lastIndexOf(' ', start);
    if (spaceIndex > start - 10) start = spaceIndex + 1;
  }
  if (end < sqlQuery.length) {
    const spaceIndex = sqlQuery.indexOf(' ', end);
    if (spaceIndex !== -1 && spaceIndex < end + 10) end = spaceIndex;
  }

  let snippet = sqlQuery.slice(start, end);
  if (start > 0) snippet = '...' + snippet;
  if (end < sqlQuery.length) snippet = snippet + '...';

  return snippet;
};
